import type { CloudUser } from '../app/cloud';
import { cloud } from '../app/cloud';
import { el } from './dom';
import { activateModal } from './modal';

export interface HandleDialogOptions {
  onUser(user: CloudUser): void;
  onSignOut?(): void | Promise<void>;
}

/** Mandatory one-time public identity setup for newly created accounts. */
export function openHandleDialog(options: HandleDialogOptions): () => void {
  document.querySelector('.handle-overlay')?.remove();
  let busy = false;
  const input = el('input', {
    class: 'onboarding-input',
    placeholder: 'your-handle',
    autocomplete: 'username',
    minlength: 3,
    maxlength: 24,
    pattern: '[a-zA-Z][a-zA-Z0-9_-]{2,23}',
    required: true,
  }) as HTMLInputElement;
  const message = el('p', {
    class: 'onboarding-message',
    role: 'alert',
    'aria-live': 'polite',
  });
  const submit = el('button', { class: 'btn primary onboarding-submit', type: 'submit' }, [
    'Set my handle',
  ]) as HTMLButtonElement;
  const form = el('form', { class: 'onboarding-form' }, [
    el('label', { class: 'onboarding-label' }, ['Public handle', input]),
    message,
    submit,
    ...(options.onSignOut
      ? [
          el(
            'button',
            {
              class: 'onboarding-switch',
              type: 'button',
              onclick: () => void options.onSignOut?.(),
            },
            ['Use another account'],
          ),
        ]
      : []),
  ]) as HTMLFormElement;
  const panel = el(
    'section',
    {
      class: 'onboarding-dialog',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'handle-onboarding-title',
    },
    [
      el('div', { class: 'auth-brand' }, [
        el('span', { class: 'auth-mark', 'aria-hidden': 'true' }, ['K']),
        el('span', {}, ['KilnCat']),
      ]),
      el('div', { class: 'onboarding-heading' }, [
        el('span', { class: 'onboarding-kicker' }, ['One last step']),
        el('h1', { id: 'handle-onboarding-title' }, ['Choose your public handle']),
        el('p', {}, [
          'Collaborators and gallery visitors will know you by this handle. Your email stays private.',
        ]),
      ]),
      form,
    ],
  );
  const overlay = el('div', { class: 'handle-overlay' }, [panel]);
  let deactivate = (): void => {};
  const close = (): void => {
    deactivate();
    overlay.remove();
  };

  form.onsubmit = (event) => {
    event.preventDefault();
    if (busy) return;
    input.value = input.value.trim().replace(/^@/, '');
    if (!input.reportValidity()) return;
    const handle = input.value;
    busy = true;
    input.disabled = true;
    submit.disabled = true;
    submit.textContent = 'Saving…';
    message.textContent = '';
    void cloud
      .updateAccount({ handle })
      .then((user) => {
        options.onUser(user);
        close();
      })
      .catch((error) => {
        message.textContent = (error as Error).message;
        busy = false;
        input.disabled = false;
        submit.disabled = false;
        submit.textContent = 'Set my handle';
        input.focus();
      });
  };

  document.body.appendChild(overlay);
  deactivate = activateModal(overlay, panel, { initialFocus: input });
  return close;
}
